import type { BusinessNotificationType, IDenormalizedPayload } from '../models/notification.model';
import { translateKbProcessingText } from './kb-processing-notification';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface ResolvedNotificationText {
  title: string;
  description: string;
  type?: BusinessNotificationType | string;
}

/** Backend sent "'<file name>' <translation_key>." for files stored before keys existed. */
const FILE_MESSAGE = /^('.*') (\w+)\.$/;

function fallbackTitle(title: string, t: Translate): string {
  if (!title) return '';
  return translateKbProcessingText(title, t) ?? (t(title) || title);
}

function fallbackDescription(description: string, t: Translate): string {
  if (!description) return '';
  const file = description.match(FILE_MESSAGE);
  if (file) return `${file[1]} ${t(file[2])}.`;
  return translateKbProcessingText(description, t) ?? (t(description) || description);
}

function keyOf(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Title and description of a bell notification in the user's language.
 * A key is translated with the backend English as its default; without a key
 * (notifications stored before keys existed) the English-text fallback applies.
 */
export function resolveNotificationText(
  payload: IDenormalizedPayload,
  t: Translate
): ResolvedNotificationText {
  const title = payload.title ?? '';
  const description = payload.description ?? '';
  const params = payload.params && typeof payload.params === 'object' ? payload.params : {};
  const titleKey = keyOf(payload.titleKey);
  const descriptionKey = keyOf(payload.descriptionKey);

  return {
    title: titleKey ? t(titleKey, { ...params, defaultValue: title }) : fallbackTitle(title, t),
    description: descriptionKey
      ? t(descriptionKey, { ...params, defaultValue: description })
      : fallbackDescription(description, t),
    type: payload.type,
  };
}
